import {GenericTable} from "@dvrt/graphql-widgets";
import {useDomainIDParam} from "@/types";
import {HasPermission, Link} from "../widgets";
import {useBoolState, useConcertoGenericQuery, useDomainNameQuery} from "@/hooks";
import CreateExtensionDialog from "./create/CreateExtensionDialog";
import FormattedButton from "@/components/FormattedButton";
import Box from "@mui/material/Box";
import TableShortcutButton from "../TableShortcutButton";
import {faMobile, faPhoneAlt, faTrash, faUser} from "@fortawesome/free-solid-svg-icons";
import {ExtensionListFragment, ExtensionListFragmentDoc} from "@/queries.generated";
import {ContextFilters} from "@/context/AuthProvider";
import {defineMessage, defineMessages} from "react-intl";
import {commonTranslations} from "@/utils/translations";

const ExtensionList = () => {
    const domainId = useDomainIDParam();

    const {domainName} = useDomainNameQuery(domainId);

    const query = useConcertoGenericQuery("users", ExtensionListFragmentDoc, {
        filtering: {
            argumentName: "filter",
            placeHolder: commonTranslations.filterPlaceholders.nameOrE164OrShortNumber,
            idPrefix: "search-extensions",
        },
        fetchPolicy: "cache-and-network",
        extraVariables: {
            domainId: domainId,
        },
        initialOrdering: ["displayName"],
    });

    const [isOpen, setShowOn, setShowOff] = useBoolState();

    return (
        <div style={{padding: 20}}>
            <GenericTable<ExtensionListFragment>
                query={query as any}
                createButton={
                    <HasPermission name="add_extension" contextFilter={ContextFilters.domain(domainName)}>
                        <FormattedButton
                            onClick={setShowOn}
                            message={defineMessage({id: "new_extension", defaultMessage: "New user"})}
                        />
                    </HasPermission>
                }
                dataRenderers={{
                    displayName: (value, row) => {
                        return <Link to={`/domains/${domainId}/users/${row.id}/`}>{value}</Link>;
                    },
                }}
                labels={defineMessages({
                    displayName: {id: "name", defaultMessage: "Name"},
                    number: {id: "number", defaultMessage: "Number"},
                    "number.shortNumber": {id: "short_number", defaultMessage: "Short number"},
                    "number.e164": {id: "e164", defaultMessage: "E.164"},
                })}
                extraColumns={[
                    {
                        path: "actions",
                        label: null,
                        renderer: (row) => {
                            return (
                                <Box sx={{display: "flex"}}>
                                    <TableShortcutButton
                                        tooltipTitle={defineMessage({
                                            id: "extension_shortcut.devices",
                                            defaultMessage: "Devices",
                                        })}
                                        shortcutPath={`${row.id}/devices/`}
                                        buttonIcon={faMobile}
                                    />
                                    <TableShortcutButton
                                        tooltipTitle={defineMessage({
                                            id: "extension_shortcut.profiles",
                                            defaultMessage: "Profiles",
                                        })}
                                        shortcutPath={`${row.id}/profiles/`}
                                        buttonIcon={faUser}
                                    />
                                    <TableShortcutButton
                                        tooltipTitle={defineMessage({
                                            id: "extension_shortcut.speed_dials",
                                            defaultMessage: "Speed dials",
                                        })}
                                        shortcutPath={`${row.id}/speed-dials/`}
                                        buttonIcon={faPhoneAlt}
                                    />
                                    <HasPermission
                                        name="delete_extension"
                                        contextFilter={ContextFilters.extension(row.number.shortNumber, domainName)}
                                    >
                                        <TableShortcutButton
                                            tooltipTitle={commonTranslations.delete}
                                            shortcutPath={`${row.id}/delete/`}
                                            buttonIcon={faTrash}
                                            color="error"
                                        />
                                    </HasPermission>
                                </Box>
                            );
                        },
                    },
                ]}
                orderableFields="all"
            />
            <HasPermission name="add_extension" contextFilter={ContextFilters.domain(domainName)}>
                <CreateExtensionDialog
                    domainId={domainId}
                    isOpen={isOpen}
                    close={setShowOff}
                    onSuccess={query.refetch}
                />
            </HasPermission>
        </div>
    );
};

export default ExtensionList;
